import { useState, useEffect, useCallback } from 'react';
import { Trash2 } from 'lucide-react';

import { api } from '../../services/client';
import { useTranslation } from '../../i18n';
import {
  useRoomMembers,
  useAssignRoomAdmin,
} from '../../hooks/queries/useSpaceQueries';
import { IRoom } from '../../types';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import UserAutocomplete from '../UserAutocomplete';

interface IRoomAssignAdminDialogProps {
  isOpen: boolean;
  onClose: () => void;
  room: IRoom | null;
}

interface IAdminEntry {
  userId: string;
  displayName: string;
  username: string;
}

const AVATAR_COLOR = '#3b82f6';

const RoomAssignAdminDialog = ({
  isOpen,
  onClose,
  room,
}: IRoomAssignAdminDialogProps) => {
  const { t } = useTranslation();
  const { data: members = [] } = useRoomMembers(room?.id ?? '');
  const assignAdmin = useAssignRoomAdmin();
  const [admins, setAdmins] = useState<IAdminEntry[]>([]);
  const [excludedUserIds, setExcludedUserIds] = useState<string[]>([]);
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Fetch space owner + admins to exclude them from the list
  useEffect(() => {
    if (!isOpen || !room?.spaceId) {
      setExcludedUserIds([]);
      return;
    }

    const loadSpaceLeaders = async () => {
      try {
        const [spaceDetail, spaceAdmins] = await Promise.all([
          api.get<{ ownerId: string }>(`/backoffice/spaces/${room.spaceId}`),
          api.get<{ id: string }[]>(
            `/backoffice/spaces/${room.spaceId}/admins`,
          ),
        ]);
        const ids = [spaceDetail.ownerId, ...spaceAdmins.map((a) => a.id)];
        setExcludedUserIds(ids);
      } catch {
        setExcludedUserIds([]);
      }
    };

    loadSpaceLeaders();
  }, [isOpen, room?.spaceId]);

  // Sync current admins when members load
  useEffect(() => {
    if (members.length > 0) {
      const currentAdmins = members
        .filter((m) => (m as any).role === 'admin')
        .map((m) => ({
          userId: m.userId,
          displayName: m.user?.displayName || m.user?.username || m.userId,
          username: m.user?.username || m.userId,
        }));
      setAdmins(currentAdmins);
    }
  }, [members]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setAdmins([]);
      setExcludedUserIds([]);
      setAutocompleteKey(0);
      setError(null);
    }
  }, [isOpen]);

  const adminUserIds = admins.map((a) => a.userId);

  const handleAddAdmin = (userId: string) => {
    if (!userId || adminUserIds.includes(userId)) return;

    const member = members.find((m) => m.userId === userId);

    if (member) {
      setAdmins((prev) => [
        ...prev,
        {
          userId: member.userId,
          displayName:
            member.user?.displayName || member.user?.username || member.userId,
          username: member.user?.username || member.userId,
        },
      ]);
    }

    setAutocompleteKey((prev) => prev + 1);
  };

  const handleRemoveAdmin = (userId: string) => {
    setAdmins((prev) => prev.filter((a) => a.userId !== userId));
  };

  const fetchRoomMembers = useCallback(
    async (search?: string) => {
      const currentAdminIds = admins.map((a) => a.userId);
      const eligible = members.filter(
        (m) =>
          !currentAdminIds.includes(m.userId) &&
          !excludedUserIds.includes(m.userId),
      );

      const filtered = search
        ? eligible.filter(
            (m) =>
              m.user?.displayName
                ?.toLowerCase()
                .includes(search.toLowerCase()) ||
              m.user?.username?.toLowerCase().includes(search.toLowerCase()),
          )
        : eligible;

      return filtered.map((m) => ({
        id: m.userId,
        username: m.user?.username || m.userId,
        displayName: m.user?.displayName || m.user?.username || m.userId,
      }));
    },
    [members, admins, excludedUserIds],
  );

  const handleConfirm = async () => {
    if (!room) return;

    setError(null);

    try {
      await assignAdmin.mutateAsync({
        roomId: room.id,
        userIds: adminUserIds,
      });
      onClose();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{t('rooms.assign_admin_title')}</DialogTitle>
          <DialogDescription>
            {t('rooms.assign_admin_desc', { name: room?.name ?? '' })}
          </DialogDescription>
        </DialogHeader>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {/* Add admin autocomplete */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-2">
            {t('rooms.select_admins')}
          </label>
          <UserAutocomplete
            key={autocompleteKey}
            value=""
            onChange={handleAddAdmin}
            fetchUsers={fetchRoomMembers}
            placeholder={t('spaces.search_users')}
            triggerPlaceholder={t('rooms.select_admin_placeholder')}
          />
        </div>

        {/* Current admins list */}
        <div className="border border-border rounded-lg divide-y divide-border max-h-72 overflow-y-auto">
          {admins.length > 0 ? (
            admins.map((admin) => (
              <div
                key={admin.userId}
                className="flex items-center gap-3 px-4 py-3"
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium shrink-0"
                  style={{ backgroundColor: AVATAR_COLOR }}
                >
                  {admin.displayName.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900">
                    {admin.displayName}
                  </div>
                  <div className="text-xs text-gray-500">@{admin.username}</div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveAdmin(admin.userId)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-400 p-4 text-center">
              {t('rooms.no_admins')}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={assignAdmin.isPending}>
            {assignAdmin.isPending
              ? t('common.saving')
              : t('rooms.confirm_assign')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RoomAssignAdminDialog;
